import { apiClient } from '../../../lib/api/client';
import { ApiError } from '../../../lib/api/error';

export type HostTenantStatus = 'Requested' | 'Approved' | 'Active' | 'Expired' | 'Rejected';

export type AccountStatus = 'Pending' | 'Active';

const STATUS_BY_INDEX: HostTenantStatus[] = ['Requested', 'Approved', 'Active', 'Expired', 'Rejected'];

export function normalizeTenantStatus(status: HostTenantStatus | number | string): HostTenantStatus {
  if (typeof status === 'number') {
    return STATUS_BY_INDEX[status] ?? 'Requested';
  }

  if (typeof status === 'string') {
    if (/^\d+$/.test(status)) {
      return STATUS_BY_INDEX[Number(status)] ?? 'Requested';
    }
    if ((STATUS_BY_INDEX as string[]).includes(status)) {
      return status as HostTenantStatus;
    }
    return 'Requested';
  }

  return status;
}

export interface SignupTenantRequest {
  companyName: string;
  primaryContactEmail: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  phone?: string;
}

export async function signupTenant(request: SignupTenantRequest): Promise<{ success: true } | { success: false; error: string }> {
  try {
    await apiClient.post('/tenants/signup', request);
    return { success: true };
  } catch (error) {
    return { success: false, error: ApiError.fromAxiosError(error).message };
  }
}

export interface HostTenantListItem {
  id: string;
  companyName: string;
  primaryContactEmail: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  phone?: string;
  status: HostTenantStatus;
  licenseEndsAtUtc?: string;
  createdAtUtc: string;
  primaryContactUserId?: string;
  primaryContactAccountStatus?: AccountStatus;
}

export interface HostDashboard {
  totalCount: number;
  requestedCount: number;
  approvedCount: number;
  activeCount: number;
  expiredCount: number;
  rejectedCount: number;
  expiringWithinWarningDaysCount: number;
  expiringSoon: { id: string; companyName: string; licenseEndsAtUtc?: string }[];
}

export interface TenantLicenseRequest {
  useDefaultTrial: boolean;
  licenseEndsAtUtc?: string;
}

export interface HostTenantProfileRequest {
  companyName: string;
  primaryContactEmail: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  phone?: string;
}

export type CreateHostTenantRequest = HostTenantProfileRequest;

export type UpdateHostTenantRequest = HostTenantProfileRequest;

const ACCOUNT_STATUS_BY_INDEX: AccountStatus[] = ['Pending', 'Active'];

function normalizeAccountStatus(status: AccountStatus | number | string | undefined): AccountStatus | undefined {
  if (status === undefined || status === null) {
    return undefined;
  }

  if (typeof status === 'number') {
    return ACCOUNT_STATUS_BY_INDEX[status];
  }

  if (typeof status === 'string' && /^\d+$/.test(status)) {
    return ACCOUNT_STATUS_BY_INDEX[Number(status)];
  }

  return status as AccountStatus;
}

type HostTenantListItemDto = Omit<HostTenantListItem, 'status' | 'primaryContactAccountStatus'> & {
  status: HostTenantStatus | number | string;
  primaryContactAccountStatus?: AccountStatus | number | string;
};

function mapHostTenant(row: HostTenantListItemDto): HostTenantListItem {
  return {
    ...row,
    status: normalizeTenantStatus(row.status),
    primaryContactAccountStatus: normalizeAccountStatus(row.primaryContactAccountStatus),
  };
}

export async function getHostTenants(status?: HostTenantStatus) {
  const response = await apiClient.get<HostTenantListItemDto[]>('/host/tenants', {
    params: status ? { status } : {},
  });
  return response.data.map(mapHostTenant);
}

export async function getHostDashboard() {
  const response = await apiClient.get<HostDashboard>('/host/dashboard');
  return response.data;
}

export async function approveTenant(tenantId: string, license: TenantLicenseRequest): Promise<void> {
  try {
    await apiClient.post(`/host/tenants/${tenantId}/approve`, license);
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

export async function rejectTenant(tenantId: string): Promise<void> {
  try {
    await apiClient.post(`/host/tenants/${tenantId}/reject`);
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

export async function expireTenant(tenantId: string) {
  await apiClient.post(`/host/tenants/${tenantId}/expire`);
}

export async function updateTenantLicense(tenantId: string, licenseEndsAtUtc: string) {
  await apiClient.patch(`/host/tenants/${tenantId}/license`, { licenseEndsAtUtc });
}

export async function updateHostTenant(
  tenantId: string,
  request: UpdateHostTenantRequest,
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    await apiClient.put(`/host/tenants/${tenantId}`, request);
    return { success: true };
  } catch (error) {
    return { success: false, error: ApiError.fromAxiosError(error).message };
  }
}

export async function createHostTenant(request: CreateHostTenantRequest): Promise<{ success: true; id: string } | { success: false; error: string }> {
  try {
    const response = await apiClient.post<{ id: string }>('/host/tenants', request);
    return { success: true, id: response.data.id };
  } catch (error) {
    return { success: false, error: ApiError.fromAxiosError(error).message };
  }
}

export async function resendTenantOnboarding(tenantId: string): Promise<void> {
  try {
    await apiClient.post(`/host/tenants/${tenantId}/resend-onboarding`);
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

export async function activateHostTenant(tenantId: string) {
  await apiClient.post(`/host/tenants/${tenantId}/activate`);
}
